import { z } from 'zod';
import type {
  AnswerDifficulty,
  AnswerKeyEntry,
} from '@/modules/diagnostico/domain/entities/answer-key.entity';
import {
  toAnswerIndex,
  toAnswerOption,
  type AnswerOption,
  type Answers,
} from './answer.entity';

/**
 * A questão como a API da Aristo devolve — **o mesmo objeto do
 * `enamed-feedback-lp`**, e o mesmo schema frouxo de lá: a rota repassa campos
 * direto do documento, e `complement`, `correctChoice` e `difficulty` podem não
 * vir. Exigi-los recusaria o ensaio inteiro por causa de uma questão
 * incompleta.
 *
 * `number` é opcional aqui: é a posição da questão na prova de origem, e no
 * simulado quem numera é o mapper (`question-ids.mapper.ts`).
 */
export const ExamQuestionSchema = z.object({
  questionId: z.string(),
  number: z.number().int().optional(),
  examOrder: z.number().optional(),
  body: z.string().default(''),
  complement: z.string().optional(),
  choices: z.array(z.string()).default([]),
  /** `0..4`, ou `-1` na anulada. Pode faltar. */
  correctChoice: z.number().int().optional(),
  correctChoiceLetter: z.string().optional(),
  isAnnulled: z.boolean().default(false),
  isEssay: z.boolean().default(false),
  institution: z.string().optional(),
  state: z.string().optional(),
  year: z.number().optional(),
  examType: z.string().optional(),
  difficulty: z.string().optional(),
  answersProportion: z.array(z.number()).default([]),
  area: z.string().optional(),
  specialty: z.string().optional(),
  theme: z.string().optional(),
  classification: z.array(z.string()).default([]),
  comment: z.string().nullable().default(null),
});

/** A resposta da busca por ids: as questões, em qualquer ordem. */
export const QuestionsResponseSchema = z.object({
  questions: z.array(ExamQuestionSchema),
});

export type ExamQuestion = z.infer<typeof ExamQuestionSchema>;
/** A questão como chega da rota, antes dos defaults do schema. */
export type ExamQuestionInput = z.input<typeof ExamQuestionSchema>;

/**
 * A questão já no ensaio: com o número da tela, que é a posição dela no
 * mapper. É o que o repositório devolve, e o que as duas telas consomem.
 */
export type SimuladoSourceQuestion = ExamQuestion & { number: number };

/**
 * A questão como aparece para quem **ainda vai responder**: enunciado e
 * alternativas, e nada mais.
 *
 * Sem resposta certa, comentário ou dificuldade de propósito — mesmo desenho do
 * `ExamQuestion` do ENAMED. Um tipo separado garante por construção que a tela
 * do ensaio não tem o gabarito para mostrar, nem escondido no DOM.
 */
export interface SimuladoQuestion {
  id: string;
  number: number;
  body: string;
  complement: string | null;
  alternatives: { letter: AnswerOption; text: string }[];
}

/** Texto vazio e os marcadores de "sem classificação" da rota viram `null`. */
export function meaningful(value: string | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === 'Sem classificação' || trimmed === 'Sem tema') {
    return null;
  }
  return trimmed;
}

/**
 * Do mapper e da resposta da API para as questões do ensaio: **na ordem do
 * mapper**, numeradas de 1 em diante.
 *
 * A rota não promete ordem, então é a lista de ids que manda. Id que a rota
 * não devolveu fica de fora e sai em `missing`, para quem chama avisar; e a
 * numeração fecha sem buraco, porque a pessoa não pode ver a "Questão 4" pular
 * para a 6. Discursiva sai junto: o ensaio é de múltipla escolha.
 */
export function toSimuladoSource(
  questionIds: readonly string[],
  questions: ExamQuestion[],
): { questions: SimuladoSourceQuestion[]; missing: string[] } {
  const byId = new Map(
    questions.map((question) => [question.questionId, question]),
  );
  const missing = questionIds.filter((id) => !byId.has(id));

  const ordered = questionIds
    .map((id) => byId.get(id))
    .filter(
      (question): question is ExamQuestion =>
        question !== undefined && !question.isEssay,
    )
    .map((question, index) => ({ ...question, number: index + 1 }));

  return { questions: ordered, missing };
}

/** As questões do ensaio, já sem o gabarito. */
export function toSimuladoQuestions(
  questions: SimuladoSourceQuestion[],
): SimuladoQuestion[] {
  return questions.map((question) => ({
    id: question.questionId,
    number: question.number,
    body: question.body,
    complement: meaningful(question.complement),
    // `flatMap` em vez de cast: alternativa além do `E` é descartada em vez
    // de mentir para o tipo.
    alternatives: question.choices.flatMap((text, index) => {
      const letter = toAnswerOption(index);
      return letter ? [{ letter, text }] : [];
    }),
  }));
}

/**
 * As respostas no formato do envio: **`questionId` → índice** da alternativa.
 *
 * Pelo id, e não pelo número da tela, porque o número é só a posição no
 * simulado — o back corrige pela questão. Em branco não tem chave.
 */
export function toAnswersByQuestionId(
  questions: Pick<SimuladoQuestion, 'id' | 'number'>[],
  answers: Answers,
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const question of questions) {
    const answer = answers[question.number];
    if (answer) result[question.id] = toAnswerIndex(answer);
  }
  return result;
}

/**
 * O caminho de volta de `toAnswersByQuestionId`: o envio lido do servidor
 * (`questionId` → índice) vira o mapa da tela (número → letra). Id que não está
 * mais no ensaio é ignorado.
 */
export function fromAnswersByQuestionId(
  questions: Pick<SimuladoQuestion, 'id' | 'number'>[],
  payload: Record<string, number>,
): Answers {
  const answers: Answers = {};
  for (const question of questions) {
    const index = payload[question.id];
    const letter = index === undefined ? null : toAnswerOption(index);
    if (letter) answers[question.number] = letter;
  }
  return answers;
}

/**
 * A dificuldade da Aristo vem em texto livre, e já apareceu de três jeitos para
 * a mesma coisa (`'Médio'`, `'Média'`, `'MEDIUM'`). Sem acento e em caixa baixa,
 * as variantes conhecidas casam; o que não casar fica sem dificuldade e sai do
 * diagnóstico como não classificada, em vez de ganhar uma faixa inventada.
 */
export function toDifficulty(
  raw: string | undefined,
): AnswerDifficulty | undefined {
  const value = raw?.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

  switch (value) {
    case 'facil':
    case 'easy':
      return 'EASY';
    case 'medio':
    case 'media':
    case 'medium':
      return 'MEDIUM';
    case 'dificil':
    case 'hard':
      return 'HARD';
    default:
      return undefined;
  }
}

/** O gabarito do diagnóstico, a partir das mesmas questões do ensaio. */
export function toAnswerKey(
  questions: SimuladoSourceQuestion[],
): AnswerKeyEntry[] {
  return questions
    .map((question) => {
      const isExcluded = question.isAnnulled || question.correctChoice === -1;
      return {
        number: question.number,
        answer:
          !isExcluded && typeof question.correctChoice === 'number'
            ? toAnswerOption(question.correctChoice)
            : null,
        isExcluded,
        difficulty: toDifficulty(question.difficulty),
      };
    })
    .sort((a, b) => a.number - b.number);
}
